import { useAction, useAtom } from "@reatom/npm-react";
import cn from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { RoutePathKeys } from "src/pages/KnowledgeBasePage/constants";

import {
  KnowledgeSidebar,
  KnowledgeHeader,
  KnowledgeDropdownToc,
  KnowledgeMarkdown,
  KnowledgeToc,
} from "./components";
import styles from "./KnowledgeBaseWidget.module.scss";
import { knowledgePage, knowledgeTree } from "./store";
import { getKBTocItems } from "./utils";
export const KnowledgeBaseWidgetLk = ({
  basePath,
  route,
}: {
  basePath: string;
  route: RoutePathKeys;
}) => {
  const navigate = useNavigate();
  const { "*": slugPath = "" } = useParams();
  const location = useLocation();
  const [activeId, setActiveId] = useState<null | string>(null);
  const [isMobileDialogOpen, setIsMobileDialogOpen] = useState(false);
  const loadTree = useAction(knowledgeTree);
  const loadPage = useAction(knowledgePage);
  const frameRef = useRef<HTMLDivElement>(null);

  const [treeData] = useAtom(knowledgeTree.dataAtom);
  const [pageData] = useAtom(knowledgePage.dataAtom);

  const tree = treeData?.tree;

  const slug = useMemo(() => {
    return slugPath.split("/").filter(Boolean).pop() ?? null;
  }, [slugPath]);

  const currentSlugPath = location.pathname.replace(basePath, "");
  const activeSectionSlug = currentSlugPath.split("/")[0];

  const itemsToc = useMemo(
    () => getKBTocItems(pageData?.content ?? ""),
    [pageData?.content],
  );

  useEffect(() => {
    if (!tree) loadTree();
  }, [tree, loadTree]);

  useEffect(() => {
    if (slug) loadPage(slug);
  }, [slug, loadPage]);

  const firstDocPath = useMemo(() => {
    if (!tree) return null;
    const firstSection = tree[0];
    const firstDoc = firstSection?.docs?.[0];
    if (!firstDoc) return null;

    return `${basePath}${firstSection.slug}/${firstDoc.slug}`;
  }, [tree, basePath]);

  useEffect(() => {
    if (!firstDocPath || pageData !== null) return;
    navigate(firstDocPath, { replace: true });
  }, [firstDocPath, pageData, navigate]);

  const handleMobileDialog = (open: boolean) => {
    setIsMobileDialogOpen(open);
    document.body.classList.toggle("modal-open", open);
  };

  if (!tree) {
    return null;
  }
  return (
    <>
      <div className={cn(styles.layoutFrame, styles.layoutFrameLk)}>
        {isMobileDialogOpen && (
          <div className={styles.mobileDialog}>
            <div className={styles.mobileDialogPadding}>
              <KnowledgeSidebar
                basePath={basePath}
                tree={tree}
                size="xl"
                currentSlugPath={currentSlugPath}
                activeSection={activeSectionSlug}
                isMobileDialogOpen={isMobileDialogOpen}
                onClick={handleMobileDialog}
              />
            </div>
          </div>
        )}
        <div className={styles.layoutHeader}>
          <KnowledgeHeader
            activeId={activeId}
            items={itemsToc}
            isMobileDialogOpen={isMobileDialogOpen}
            onClick={handleMobileDialog}
          />
        </div>
        <div className={styles.layoutMenu}>
          <div className={styles.layoutMenuPosition}>
            <KnowledgeSidebar
              basePath={basePath}
              tree={tree}
              size="xl"
              currentSlugPath={currentSlugPath}
              activeSection={activeSectionSlug}
            />
          </div>
        </div>
        <div className={styles.frame}>
          {pageData && (
            <>
              <div className={styles.content} ref={frameRef}>
                <div className={styles.tocMobile}>
                  <KnowledgeDropdownToc activeId={activeId} items={itemsToc} />
                </div>
                <KnowledgeMarkdown
                  framerRef={frameRef}
                  basePath={basePath}
                  content={pageData.content}
                  onActiveChange={setActiveId}
                  route={route}
                />
              </div>
              <div className={styles.tocPc}>
                <KnowledgeToc
                  items={itemsToc}
                  activeId={activeId}
                  route={route}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
